import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { z } from "zod";
import { ArrowLeft, CheckCircle2, Circle, Lock } from "lucide-react";
import { authService } from "@/services/authService";
import { PASSWORD_MIN_LENGTH, evaluatePasswordRules } from "@/lib/passwordPolicy";
import { Alert, Button, PasswordInput, Spinner } from "@/components/ui";
import { AuthShell } from "@/components/common";
import { getApiErrorMessage, toastApiError } from "@/lib/errorMessages";
import { cn } from "@/lib/utils";

function createSetPasswordSchema(t: (k: string, o?: Record<string, unknown>) => string) {
  return z
    .object({
      password: z
        .string()
        .min(PASSWORD_MIN_LENGTH, t("setPasswordMinLength", { min: PASSWORD_MIN_LENGTH })),
      password_confirm: z.string().min(1, t("passwordConfirmRequired")),
    })
    .superRefine((data, ctx) => {
      const { password } = data;
      if (password.length < PASSWORD_MIN_LENGTH) return;
      const r = evaluatePasswordRules(password);
      if (!r.mixedCase) {
        ctx.addIssue({
          code: "custom",
          message: t("setPasswordMixedCase"),
          path: ["password"],
        });
      }
      if (!r.digit) {
        ctx.addIssue({
          code: "custom",
          message: t("setPasswordRequireDigit"),
          path: ["password"],
        });
      }
      if (!r.special) {
        ctx.addIssue({
          code: "custom",
          message: t("setPasswordRequireSpecial"),
          path: ["password"],
        });
      }
    })
    .refine((data) => data.password === data.password_confirm, {
      message: t("passwordsDoNotMatch"),
      path: ["password_confirm"],
    });
}

type SetPasswordForm = z.infer<ReturnType<typeof createSetPasswordSchema>>;

function LiveRequirementRow({ met, children }: { met: boolean; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-[13px]">
      {met ? (
        <CheckCircle2
          className="mt-0.5 h-4 w-4 shrink-0 text-success-600"
          aria-hidden
        />
      ) : (
        <Circle
          className="mt-0.5 h-4 w-4 shrink-0 text-surface-300"
          aria-hidden
        />
      )}
      <span
        className={cn(
          "leading-snug",
          met ? "text-success-700" : "text-surface-600",
        )}
      >
        {children}
      </span>
    </li>
  );
}

export default function SetPasswordPage() {
  const { t } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const schema = useMemo(() => createSetPasswordSchema(t), [t]);
  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SetPasswordForm>({
    resolver,
    mode: "onChange",
    defaultValues: {
      password: "",
      password_confirm: "",
    },
  });

  const passwordValue = watch("password");
  const ruleState = useMemo(
    () => evaluatePasswordRules(passwordValue),
    [passwordValue],
  );

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setChecking(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { valid } = await authService.validatePasswordSetupToken(token);
        if (!cancelled) {
          setTokenValid(valid);
        }
      } catch {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (data: SetPasswordForm) => {
    if (!token) return;
    try {
      setIsLoading(true);
      await authService.completePasswordSetup({
        token,
        password: data.password,
        password_confirm: data.password_confirm,
      });
      setDone(true);
      toast.success(t("setPasswordSuccessToast"));
    } catch (err) {
      const msg = getApiErrorMessage(err, "setPasswordFailed");
      if (/too\s+common/i.test(msg)) {
        toast.error(t("setPasswordTooCommonToast"));
      } else {
        toastApiError(err, "setPasswordFailed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      footer={<p>{tCommon("copyright", { year: new Date().getFullYear() })}</p>}
    >
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-surface-900">
          {t("setPasswordTitle")}
        </h1>
      </div>

      {checking ? (
        <div className="flex items-center gap-2.5 text-sm text-surface-500">
          <Spinner size="sm" loadingLabel={t("setPasswordChecking")} />
          {t("setPasswordChecking")}
        </div>
      ) : !token || tokenValid === false ? (
        <Alert variant="warning">{t("setPasswordInvalidLink")}</Alert>
      ) : done ? (
        <Alert variant="success">{t("setPasswordDone")}</Alert>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <PasswordInput
            id="set-password-new"
            {...register("password")}
            label={t("newPassword")}
            placeholder={t("passwordPlaceholder")}
            error={errors.password?.message}
            autoComplete="new-password"
            leftIcon={<Lock className="h-4 w-4" />}
            showPasswordLabel={t("showPassword")}
            hidePasswordLabel={t("hidePassword")}
          />
          <div
            className="rounded-lg border border-surface-200 bg-surface-50 px-4 py-3"
            role="region"
            aria-label={t("setPasswordRequirementsTitle")}
          >
            <p className="text-xs font-medium text-surface-500">
              {t("setPasswordRequirementsTitle")}
            </p>
            <ul className="mt-2 list-none space-y-1.5" role="list">
              <LiveRequirementRow met={ruleState.minLength}>
                {t("setPasswordRequirementMinLength", {
                  min: PASSWORD_MIN_LENGTH,
                })}
              </LiveRequirementRow>
              <LiveRequirementRow met={ruleState.mixedCase}>
                {t("setPasswordRequirementMixedCase")}
              </LiveRequirementRow>
              <LiveRequirementRow met={ruleState.digit}>
                {t("setPasswordRequirementDigit")}
              </LiveRequirementRow>
              <LiveRequirementRow met={ruleState.special}>
                {t("setPasswordRequirementSpecial")}
              </LiveRequirementRow>
            </ul>
          </div>
          <PasswordInput
            id="set-password-confirm"
            {...register("password_confirm")}
            label={t("confirmPassword")}
            placeholder={t("passwordPlaceholder")}
            error={errors.password_confirm?.message}
            autoComplete="new-password"
            leftIcon={<Lock className="h-4 w-4" />}
            showPasswordLabel={t("showConfirmPassword")}
            hidePasswordLabel={t("hideConfirmPassword")}
          />
          <Button
            type="submit"
            className="w-full"
            size="lg"
            isLoading={isLoading}
          >
            {t("setPasswordSubmit")}
          </Button>
        </form>
      )}

      <div className="mt-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("backToLogin")}
        </Link>
      </div>
    </AuthShell>
  );
}
